import { CalendarDays, Inbox, Menu, Plus, Sun, X } from 'lucide-react';
import { useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { Skeleton } from '@/components/ui';
import { useMe, useNodes } from '@/lib/hooks';
import { openCapture } from '@/lib/ui-events';
import { cn } from '@/lib/utils';
import { CommandPalette } from './command-palette';
import { DetailPanel } from './detail-panel';
import { MoveDialog } from './move-dialog';
import { NewNodeDialog } from './new-node-dialog';
import { QuickCapture } from './quick-capture';
import { Logo, Sidebar } from './sidebar';

function TabLink({
  to,
  icon,
  label,
  end,
  badge,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
  end?: boolean;
  badge?: number;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium text-muted-fg',
          isActive && 'text-primary',
        )
      }
    >
      {icon}
      {label}
      {badge ? (
        <span className="absolute right-[28%] top-1 rounded-full bg-primary px-1 text-[10px] leading-4 text-primary-fg">
          {badge}
        </span>
      ) : null}
    </NavLink>
  );
}

export function AppShell() {
  const { data: me, isLoading, error } = useMe();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const fullBleed = location.pathname === '/map' || location.pathname === '/calendar';
  const { data: inbox } = useNodes({ parent: 'none', type: ['task', 'idea'] });
  if (isLoading)
    return (
      <div className="mx-auto max-w-2xl space-y-3 p-8">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24" />
        <Skeleton className="h-24" />
      </div>
    );
  if (error || !me)
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return (
    <div className="flex h-full">
      <div className="hidden w-64 shrink-0 border-r border-border bg-surface/60 md:block">
        <Sidebar />
      </div>
      {navOpen && (
        <div className="fixed inset-0 z-40 flex animate-fade-in md:hidden">
          <div className="relative w-72 max-w-[85%] animate-slide-in border-r border-border bg-bg">
            <button
              className="absolute right-2 top-3 z-10 rounded-lg p-1.5 text-muted-fg hover:bg-muted"
              aria-label="Close menu"
              onClick={() => setNavOpen(false)}
            >
              <X size={18} />
            </button>
            <Sidebar onNavigate={() => setNavOpen(false)} />
          </div>
          <button className="flex-1 bg-black/40" aria-label="Close menu" onClick={() => setNavOpen(false)} />
        </div>
      )}
      <main className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center justify-between border-b border-border bg-surface/80 px-3 py-2 backdrop-blur md:hidden">
          <div className="flex items-center gap-2 font-semibold">
            <Logo size={24} /> GoalBuddy
          </div>
          <button
            className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted"
            onClick={() => setNavOpen(true)}
            aria-label="Menu"
          >
            <Menu size={20} />
          </button>
        </div>
        {fullBleed ? (
          <div key={location.pathname} className="min-h-0 flex-1 animate-rise pb-16 md:pb-0">
            <Outlet />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-4 pb-24 pt-6 md:px-10 md:pb-10 md:pt-8">
            <div key={location.pathname} className="mx-auto max-w-4xl animate-rise">
              <Outlet />
            </div>
          </div>
        )}
        <nav className="pb-safe fixed inset-x-0 bottom-0 z-30 flex items-end border-t border-border bg-surface/95 backdrop-blur md:hidden">
          <TabLink to="/" end icon={<Sun size={20} />} label="Today" />
          <TabLink to="/inbox" icon={<Inbox size={20} />} label="Inbox" badge={inbox?.length} />
          <div className="flex flex-1 justify-center">
            <button
              onClick={openCapture}
              aria-label="Quick capture"
              className="-mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-fg shadow-[var(--shadow-pop)] active:scale-95"
            >
              <Plus size={24} />
            </button>
          </div>
          <TabLink to="/calendar" icon={<CalendarDays size={20} />} label="Calendar" />
          <button
            onClick={() => setNavOpen(true)}
            className="flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium text-muted-fg"
          >
            <Menu size={20} />
            More
          </button>
        </nav>
      </main>
      <DetailPanel />
      <QuickCapture />
      <CommandPalette />
      <MoveDialog />
      <NewNodeDialog />
    </div>
  );
}
